import {
  getButtonContainers,
  getButtons,
  getForms,
  getGreetingsButtons,
  getMessageFeed,
  getnewUserForm,
  getFirstNameErrorMessage,
  getFirstNameInput,
  getPhoneNumberErrorMessage,
  getPhoneNumberInput,
  getLastNameInput,
  getMinMaxButtonContainer,
  getLastNameErrorMessage,
} from '@/components/chatbot/chatbotElementUtils';
import {
  removeGreetingElement,
  setUpMaximizeButton,
  setUpMinimizeButton,
  switchMinMaxButtonVisibility,
} from '@/components/chatbot/chatbotMinMaxButtonsController';
import {
  disableMessageInput,
  enableMessageInput,
  handleUserMessage,
  setPlaceHolderContent,
} from './chatbotMessagingController';
import { setGreetingView } from '@/components/chatbot/chatbotConversationPersitenceUtils';

function setUpMinMaxButtons() {
  setUpMinimizeButton();
  setUpMaximizeButton();
}

function setUpGreetingButtons() {
  const greetingsBtns = getGreetingsButtons();
  greetingsBtns.forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      const minMaxButtonContainer = getMinMaxButtonContainer();
      setGreetingView();
      if (btn.dataset.book) {
        window.bookAVisitClicked = true;
      }
      minMaxButtonContainer.classList.remove('solv-chatbot-widget--minimized');
      minMaxButtonContainer.classList.add('solv-chatbot-widget--maximized');
      removeGreetingElement();
      switchMinMaxButtonVisibility();
      const firstNameInput = getFirstNameInput();
      firstNameInput.focus();
    });
  });
}

function autoScrollToBottom() {
  const messageFeed = getMessageFeed();

  setTimeout(() => {
    messageFeed.scrollIntoView(false);
  }, 500);
}

function setUpButtons() {
  const buttons = getButtons();
  buttons.forEach((button) => {
    button.addEventListener('click', async (e) => {
      if (button.dataset.viewMoreLocations) {
        handleViewMoreLocationsButtonClick();
        button.remove();
      } else {
        const message = button.dataset.buttonContent;
        if (message) {
          await handleUserMessage(message);
        }
      }
    });
  });
}

function handleViewMoreLocationsButtonClick() {
  const hiddenLocations = document.querySelectorAll('[data-hidden-location]');
  hiddenLocations.forEach((location) => {
    location.classList.remove('hidden');
  });
}

async function submitUserInfo() {
  const firstName = getFirstNameInput().value;
  const lastName = getLastNameInput().value;
  const phone = getPhoneNumberInput().value;

  const userInformation = {
    first_name: firstName,
    last_name: lastName,
    phone_number: phone,
  };

  let message = `<strong>First Name</strong>: ${firstName}, <br> <strong>Last Name</strong>: ${lastName}, <br> <strong>Phone</strong>: ${phone},`;

  if (window.bookAVisitClicked) {
    message = `${message} <br> I would like to book a visit`;
  }

  setPlaceHolderContent('replyInput');
  enableMessageInput();

  await handleUserMessage(message, userInformation);
}

function removeForms() {
  const forms = getForms();
  forms.forEach((form) => {
    form.remove();
  });
}

function isValidPhoneNumber() {
  const phoneNumberInput = getPhoneNumberInput();
  const phoneRegExp =
    /^[\+]?[(]?[0-9]{3}[)]?[+\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
  return phoneRegExp.test(phoneNumberInput.value);
}

function isValidName(inputElement) {
  const nameRegExp = /^\s*$/;
  return !nameRegExp.test(inputElement.value);
}

function showErrorMessage(inputElement, messageElement) {
  inputElement.classList.add('invalid');
  inputElement.ariaInvalid = true;
  messageElement.classList.remove('solv-chatbot-widget--hidden');
  messageElement.ariaHidden = false;
}

function hideErrorMessage(inputElement, messageElement) {
  inputElement.classList.remove('invalid');
  inputElement.ariaInvalid = false;
  messageElement.classList.add('solv-chatbot-widget--hidden');
  messageElement.ariaHidden = true;
}

const isNumericInput = (event) => {
  const key = event.keyCode;
  return (
    (key >= 48 && key <= 57) || // Allow number line
    (key >= 96 && key <= 105) // Allow number pad
  );
};

const isModifierKey = (event) => {
  const key = event.keyCode;
  return (
    event.shiftKey === true ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 8 ||
    key === 9 ||
    key === 13 ||
    key === 46 || // Allow Backspace, Tab, Enter, Delete
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey === true || event.metaKey === true) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
  );
};

const enforceFormat = (event) => {
  // Input must be of a valid number format or a modifier key, and not longer than ten digits
  if (!isNumericInput(event) && !isModifierKey(event)) {
    event.preventDefault();
  }
};

const formatToPhone = (event) => {
  if (isModifierKey(event)) {
    return;
  }

  const input = event.target.value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
  const areaCode = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);

  if (input.length > 6) {
    event.target.value = `(${areaCode}) ${middle}-${last}`;
  } else if (input.length > 3) {
    event.target.value = `(${areaCode}) ${middle}`;
  } else if (input.length > 0) {
    event.target.value = `(${areaCode}`;
  }
};

function setUpNewUserForm() {
  const newUserForm = getnewUserForm();
  const phoneNumberInput = getPhoneNumberInput();
  const firstNameInput = getFirstNameInput();
  const lastNameInput = getLastNameInput();

  if (!newUserForm) return;

  setPlaceHolderContent('phoneInput');

  disableMessageInput();

  phoneNumberInput.addEventListener('input', (e) => {
    enforceFormat(e);
    formatToPhone(e);

    // This is to help avoid confusing the user if they've inputted an incorrect value. We don't want to show the error anymore if they input a valid value.
    if (isValidPhoneNumber()) {
      hideErrorMessage(phoneNumberInput, getPhoneNumberErrorMessage());
    } else {
      showErrorMessage(phoneNumberInput, getPhoneNumberErrorMessage());
    }
  });

  firstNameInput.addEventListener('input', (e) => {
    // This is to help avoid confusing the user if they've inputted an incorrect value. We don't want to show the error anymore if they input a valid value.
    if (isValidName(firstNameInput)) {
      hideErrorMessage(firstNameInput, getFirstNameErrorMessage());
    } else {
      showErrorMessage(firstNameInput, getFirstNameErrorMessage());
    }
  });

  lastNameInput.addEventListener('input', (e) => {
    // This is to help avoid confusing the user if they've inputted an incorrect value. We don't want to show the error anymore if they input a valid value.
    if (isValidName(lastNameInput)) {
      hideErrorMessage(lastNameInput, getLastNameErrorMessage());
    } else {
      showErrorMessage(lastNameInput, getLastNameErrorMessage());
    }
  });

  newUserForm.addEventListener('submit', async (e) => {
    e.preventDefault();

    const phoneValid = isValidPhoneNumber();
    const firstNameValid = isValidName(getFirstNameInput());
    const lastNameValid = isValidName(getLastNameInput());

    if (phoneValid && firstNameValid && lastNameValid) {
      await submitUserInfo();
    } else {
      if (!phoneValid) {
        showErrorMessage(phoneNumberInput, getPhoneNumberErrorMessage());
      }

      if (!firstNameValid) {
        showErrorMessage(firstNameInput, getFirstNameErrorMessage());
      }

      if (!lastNameValid) {
        showErrorMessage(lastNameInput, getFirstNameErrorMessage());
      }
    }
  });
}

function removeButtons() {
  const buttonContainers = getButtonContainers();
  buttonContainers.forEach((container) => {
    container.remove();
  });
}

export {
  setUpButtons,
  setUpGreetingButtons,
  setUpMinMaxButtons,
  setUpNewUserForm,
  removeButtons,
  removeForms,
  autoScrollToBottom,
};
